import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { shouldForwardProp } from 'commons/utils/mui'
import { theme } from 'styles/theme/hav-theme'

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xm};
  padding: ${theme.spacing.xm};
  border-radius: ${theme.shapes.borderRadius.xm};
  background-color: ${theme.colors.neutral.white};
`

export const Content = styled.div`
  display: grid;
  gap: ${theme.spacing.sm};
  grid-template-columns: 315px 1fr;

  .carteira {
    grid-area: carteira;
  }
  .cestas {
    grid-area: cestas;
  }
  .produtos {
    grid-area: produtos;
  }

  grid-template-areas: 'carteira cestas' 'carteira produtos';

  @media (max-width: ${theme.breakpoints.lg}px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'carteira' 'cestas' 'produtos';
  }
`

type SectionCardProps = {
  faded?: boolean
}
export const SectionCard = styled.div<SectionCardProps>`
  display: flex;
  flex-direction: column;
  border-radius: ${theme.shapes.borderRadius.xm};
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.xm};
  background: ${theme.colors.neutral.white};
  box-shadow: ${theme.effects.boxShadow['rule-01']};

  ${({ faded }) =>
    faded &&
    css`
      p,
      span {
        color: ${theme.colors.neutral.light} !important;
      }

      svg {
        path {
          fill: ${theme.colors.neutral.light} !important;
        }
      }
    `}
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const TitleCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

export const RowDivider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${theme.colors.neutral.clear};
`

export const WrapDetailsButton = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${theme.spacing.sm};
`

export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

export const ResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.lg}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacing.xl};
  }
`

type ActionLinkProps = {
  loading: boolean
  disabled?: boolean
}

export const ActionLink = styled('div', { shouldForwardProp })<ActionLinkProps>`
  span {
    cursor: pointer;
    color: ${theme.colors.primary.main};
    font-size: ${theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.xl};
    transition: 0.3s;

    :hover {
      color: ${theme.colors.primary.hover};
    }
  }

  ${({ loading }) =>
    loading &&
    css`
      cursor: wait;
      span {
        pointer-events: none;
        color: ${theme.colors.neutral.light};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      span {
        pointer-events: none;
        color: ${theme.colors.neutral.light};
      }
    `}
`

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

export const GraphRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  min-width: 275px;
`

export const GraphLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 275px;
  height: 150px;
  transform: scale(0.5);
`

export const TextRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const ResultsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
`

export const HozDivider = styled.div`
  height: 1px;
  background: ${theme.colors.quartenary};
  width: 100%;
`

export const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.lg}px) {
    margin: 0;
  }
`

export const VertDivider = styled.div`
  width: 1px;
  height: 100%;
  min-height: 44px;
  max-height: 64px;
  background: ${theme.colors.quartenary};
`

export const DataCol = styled.div`
  display: flex;
  flex: 1;
  max-width: 225.5px;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

export const DataNumber = styled(DataCol)`
  gap: 0;
`

export const TitleCard = styled(SectionCard)`
  padding: ${theme.spacing.md};
  box-shadow: none;
  padding: 0;
`

export const StrategyGrid = styled.div`
  display: grid;
  row-gap: 20px;
  column-gap: 30px;
  grid-template-columns: repeat(4, 1fr);

  .grid-item {
    position: relative;

    /* Horizontal dividers */
    :nth-of-type(n + 5)::before {
      content: '';
      position: absolute;
      top: -10px;
      left: 0;
      width: 100%;
      height: 1px;
      background: ${theme.colors.quartenary};
    }

    /* Ensure the first row does not have a horizontal divider */
    .grid-item:nth-of-type(-n + 4)::before {
      content: none;
    }

    /* Vertical dividers */
    :after {
      content: '';
      position: absolute;
      width: 1px;
      top: 10%;
      bottom: 10%;
      right: -15px;
      background: ${theme.colors.quartenary};
    }

    /* Remove vertical divider for the last item in each row */
    :nth-of-type(4n)::after {
      content: none;
    }
  }
`
